import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function n(v){const x=Number(v);return v!==null&&v!==undefined&&v!==''&&Number.isFinite(x)?x:null;}
function ratio(a,b){a=n(a);b=n(b);return a!==null&&b!==null&&b!==0?a/b:null;}
function pct(a,b){const r=ratio(a,b);return r===null?null:r*100;}
function metric(snapshot,key){return snapshot?.metrics?.[key]?.value??null;}
function provenance(snapshot,key){
  const m=snapshot?.metrics?.[key];
  if(!m||m.status!=='VERIFIED') return null;
  return {
    sourceFact:m.sourceFact||null,
    filed:m.row?.filed||null,
    form:m.row?.form||null,
    end:m.row?.end||null,
    accession:m.row?.accession||null,
    unit:m.row?.unit||null
  };
}

function derive(snapshot={}){
  const revenue=n(metric(snapshot,'revenue'));
  const netIncome=n(metric(snapshot,'netIncome'));
  const operatingIncome=n(metric(snapshot,'operatingIncome'));
  const assets=n(metric(snapshot,'assets'));
  const liabilities=n(metric(snapshot,'liabilities'));
  const equity=n(metric(snapshot,'equity'));
  const cash=n(metric(snapshot,'cash'));
  const ocf=n(metric(snapshot,'operatingCashFlow'));
  const capex=n(metric(snapshot,'capex'));
  const totalDebt=n(snapshot?.derived?.totalDebt?.value);

  const fcf=ocf!==null&&capex!==null?ocf-capex:null;

  return Object.freeze({
    version:VERSION,
    symbol:snapshot.symbol||null,
    factualMetrics:Object.freeze({
      debtToEquity:ratio(totalDebt,equity),
      debtToAssets:ratio(totalDebt,assets),
      liabilitiesToAssets:ratio(liabilities,assets),
      cashToDebt:ratio(cash,totalDebt),
      netMarginPct:pct(netIncome,revenue),
      operatingMarginPct:pct(operatingIncome,revenue),
      returnOnAssetsPct:pct(netIncome,assets),
      returnOnEquityPct:pct(netIncome,equity),
      operatingCashFlowMarginPct:pct(ocf,revenue),
      freeCashFlow:fcf,
      freeCashFlowMarginPct:pct(fcf,revenue),
      cashConversionNetIncome:ratio(ocf,netIncome)
    }),
    sourceValues:Object.freeze({
      revenue,netIncome,operatingIncome,assets,liabilities,equity,cash,totalDebt,
      operatingCashFlow:ocf,capex
    }),
    provenance:Object.freeze({
      revenue:provenance(snapshot,'revenue'),
      netIncome:provenance(snapshot,'netIncome'),
      operatingIncome:provenance(snapshot,'operatingIncome'),
      assets:provenance(snapshot,'assets'),
      liabilities:provenance(snapshot,'liabilities'),
      equity:provenance(snapshot,'equity'),
      cash:provenance(snapshot,'cash'),
      operatingCashFlow:provenance(snapshot,'operatingCashFlow'),
      capex:provenance(snapshot,'capex')
    })
  });
}

function toFinancialHealthBridge(snapshot={}){
  const d=derive(snapshot);
  const groups={
    balanceSheet:['debtToEquity','debtToAssets','liabilitiesToAssets'],
    cashGeneration:['operatingCashFlowMarginPct','freeCashFlow','freeCashFlowMarginPct','cashConversionNetIncome'],
    profitability:['netMarginPct','operatingMarginPct','returnOnAssetsPct','returnOnEquityPct'],
    debtService:['cashToDebt'],
    liquidity:['cashToDebt']
  };
  const coverage={};
  for(const [k,names] of Object.entries(groups)){
    const present=names.filter(name=>d.factualMetrics[name]!==null).length;
    coverage[k]={present,total:names.length,ratio:names.length?present/names.length:0};
  }

  return Object.freeze({
    version:VERSION,
    symbol:d.symbol,
    state:Object.values(coverage).some(x=>x.present>0)?'REVIEW_ONLY':'MISSING',
    autoScore:false,
    calibrated:false,
    financialHealthInputs:Object.freeze({
      balanceSheet:null,
      cashGeneration:null,
      profitability:null,
      debtService:null,
      liquidity:null
    }),
    factualMetrics:d.factualMetrics,
    sourceValues:d.sourceValues,
    provenance:d.provenance,
    coverage:Object.freeze(coverage),
    note:'SEC facts are converted into factual ratios only. No 0–10 Financial Health score is invented before calibration.'
  });
}

function selfTest(){
  const t=[],ok=(name,p)=>t.push({name,pass:!!p});
  const row={filed:'2026-02-01',form:'10-K',end:'2025-12-31',accession:'x',unit:'USD'};
  const snap={
    symbol:'AAA',
    metrics:{
      revenue:{value:100,status:'VERIFIED',sourceFact:'Revenues',row},
      netIncome:{value:10,status:'VERIFIED',sourceFact:'NetIncomeLoss',row},
      operatingIncome:{value:15,status:'VERIFIED',sourceFact:'OperatingIncomeLoss',row},
      assets:{value:200,status:'VERIFIED',sourceFact:'Assets',row},
      liabilities:{value:80,status:'VERIFIED',sourceFact:'Liabilities',row},
      equity:{value:120,status:'VERIFIED',sourceFact:'StockholdersEquity',row},
      cash:{value:30,status:'VERIFIED',sourceFact:'CashAndCashEquivalentsAtCarryingValue',row},
      operatingCashFlow:{value:20,status:'VERIFIED',sourceFact:'NetCashProvidedByUsedInOperatingActivities',row},
      capex:{value:5,status:'VERIFIED',sourceFact:'PaymentsToAcquirePropertyPlantAndEquipment',row}
    },
    derived:{totalDebt:{value:40,status:'DERIVED_VERIFIED'}}
  };
  const d=derive(snap);
  ok('symbol preserved',d.symbol==='AAA');
  ok('debt/equity derived',Math.abs(d.factualMetrics.debtToEquity-1/3)<1e-9);
  ok('debt/assets derived',d.factualMetrics.debtToAssets===0.2);
  ok('liabilities/assets derived',d.factualMetrics.liabilitiesToAssets===0.4);
  ok('cash/debt derived',d.factualMetrics.cashToDebt===0.75);
  ok('net margin derived',d.factualMetrics.netMarginPct===10);
  ok('operating margin derived',d.factualMetrics.operatingMarginPct===15);
  ok('ROA derived',d.factualMetrics.returnOnAssetsPct===5);
  ok('ROE derived',Math.abs(d.factualMetrics.returnOnEquityPct-8.333333333333332)<1e-9);
  ok('OCF margin derived',d.factualMetrics.operatingCashFlowMarginPct===20);
  ok('FCF derived',d.factualMetrics.freeCashFlow===15);
  ok('FCF margin derived',d.factualMetrics.freeCashFlowMarginPct===15);
  ok('cash conversion derived',d.factualMetrics.cashConversionNetIncome===2);
  ok('provenance preserved',d.provenance.revenue.sourceFact==='Revenues');
  ok('filing date preserved',d.provenance.revenue.filed==='2026-02-01');

  const b=toFinancialHealthBridge(snap);
  ok('bridge review only',b.state==='REVIEW_ONLY');
  ok('bridge never auto scores',b.autoScore===false);
  ok('bridge explicitly uncalibrated',b.calibrated===false);
  ok('all engine score inputs remain null',Object.values(b.financialHealthInputs).every(x=>x===null));
  ok('profitability coverage complete',b.coverage.profitability.present===4);

  const missing=toFinancialHealthBridge({symbol:'X',metrics:{},derived:{}});
  ok('missing facts remain missing',missing.state==='MISSING');
  ok('missing ratios remain null',missing.factualMetrics.netMarginPct===null);
  ok('missing debt does not become zero',missing.sourceValues.totalDebt===null);

  const zeroDebt=derive({...snap,derived:{totalDebt:{value:0}}});
  ok('zero debt cash/debt stays null not infinity',zeroDebt.factualMetrics.cashToDebt===null);

  const api={VERSION,derive,toFinancialHealthBridge};
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestSECFinancialHealthInputBridge=Object.freeze({VERSION,derive,toFinancialHealthBridge,selfTest});
})(engineContext);